<template>
  <section class="ranking-page">
    <div class="page-header">
      <p class="eyebrow">Engineer Comparison</p>
      <h2>AI Sizing Comparison</h2>
      <p>
        Compare saved reference designs with the current AI workflow, inspect equipment and calculated trade-offs, and record trace-linked reviewer judgments. Quote success and equipment similarity are evidence—not automatic measures of engineering quality.
      </p>
    </div>

    <Card>
      <template #title>Select Engineer Sizing</template>
      <template #content>
        <div class="sizing-picker">
          <label>
             Reference scenario (linked quotes included)
            <Dropdown
              v-model="selectedSizingId"
              :options="completedSizingOptions"
              option-label="label"
              option-value="id"
              placeholder="Select a reference scenario"
              filter
              show-clear
              :loading="isLoadingSizings"
              @change="selectCompletedSizing"
            />
            <small v-if="sizingListError" class="list-error">
              {{ sizingListError }} You can still paste an ID below.
            </small>
            <small v-else>
              {{ referenceCount }} matching scenarios. Selection uses the exact quoted scenario, not the first scenario in a sizing. Saved AI provenance is labeled when present; its absence does not prove human authorship.
            </small>
          </label>
          <Button
            label="Refresh list"
            class="p-button-text refresh-button"
            :loading="isLoadingSizings"
            @click="loadCompletedSizingOptions"
          />
        </div>
        <div class="form-grid">
          <label>Cohort<Dropdown v-model="cohort" :options="cohorts" option-label="label" option-value="value" @change="loadCompletedSizingOptions" /></label>
          <label>Search sizing / scenario<InputText v-model="referenceSearch" @keyup.enter="loadCompletedSizingOptions" /></label>
          <RouterLink :to="{ name: 'AiDosSizingParity' }">Calculation parity diagnostics (browser vs server)</RouterLink>
        </div>
        <div class="form-grid">
          <label>
            Sizing or Scenario ID
            <InputText
              v-model="sizingOrScenarioId"
              placeholder="sizing or scenario uuid"
            />
            <small>
              Paste either the sizing ID or the scenario ID from
              `/sizings/:sizingId/scenarios/:scenarioId/...`.
            </small>
          </label>
          <label>
            Generation method
            <span>Current recommendation endpoint / durable worker workflow</span>
            <small>Uses the deployed planner configuration; records model/plan evidence returned by the job.</small>
          </label>
        </div>
        <Button
          label="Generate AI Sizing and Compare"
          class="p-button-success mt-3"
          :loading="isRunning"
          :disabled="!sizingOrScenarioId"
          @click="runValidation"
        />
      </template>
    </Card>

    <p v-if="isRunning" role="status">{{ comparisonProgress || 'Preparing comparison' }}</p>
    <details v-if="laneDiagnostics"><summary>All strategy attempts (including unsuccessful lanes)</summary><AdminJsonViewer :value="laneDiagnostics" /></details>
    <Card v-if="diagnosticOptions.length" class="mt-4">
      <template #title>Generated design diagnostics</template>
      <template #content>
        <details v-for="option in diagnosticOptions" :key="option.priority">
          <summary>{{ option.priority }} · {{ option.traceId }}</summary>
          <AIDesignDiagnostics :option="option" />
        </details>
      </template>
    </Card>
    <Card v-if="comparisonReady" class="mt-4">
      <template #title>Reviewer assessment</template>
      <template #content>
        <p>Same equipment is not necessarily better. Compare operating targets, validation findings and strategy intent before assigning a judgment. Labels are review evidence; they do not automatically train a model.</p>
        <p>Baseline validation: {{ baselineCalculation?.status }} · {{ baselineCalculation?.hardFailures.length }} hard failures. {{ baselineCalculation?.warnings.length }} warnings.</p>
        <div v-for="lane in availableComparisonLanes" :key="lane" class="warning-banner">
          {{ laneLabel(lane) }}: {{ equipmentComparisonRows.filter(row => row.engineer.ids.length || row.ai[lane]?.ids.length).filter(row => selectionsMatch(row.engineer, row.ai[lane])).length }} exact assembly selections matched · {{ topOptions[lane]?.hardFailures.length }} recorded hard failures.
        </div>
        <div class="form-grid">
          <label>AI lane<Dropdown v-model="reviewLane" :options="availableComparisonLanes" /></label>
          <label>Overall judgment<Dropdown v-model="verdict" :options="verdicts" /></label>
          <label>Dataset curation<Dropdown v-model="curation" :options="curations" /></label>
          <label>Flags<MultiSelect v-model="reviewFlags" :options="flagOptions" /></label>
          <label>Reasoning<textarea v-model="reviewNotes" maxlength="4000" rows="4" /></label>
        </div>
        <Button label="Save reviewer assessment" :loading="savingReview" :disabled="!reviewNotes.trim() || !comparisonJobId" @click="saveAssessment" />
        <p v-if="reviewMessage" role="status">{{ reviewMessage }}</p>
        <p>Curation “candidate” requires further dataset approval. Won-quote cohorts can contain selection bias and are not field run-life outcomes.</p>
      </template>
    </Card>
    <Card v-if="reviews.length" class="mt-4">
      <template #title>Saved assessments for this scenario</template>
      <template #content>
        <Button label="Copy review dataset JSON" @click="copyJson({ version: 'sizing-review-v1', trainingApproved: false, reviews })" />
        <ul><li v-for="review in reviews" :key="String(review.id)">{{ review.created_at }} · {{ review.reviewer_id }} · {{ review.lane }} · {{ review.verdict }} · {{ review.curation }} — {{ review.notes }}</li></ul>
      </template>
    </Card>

    <div v-if="error" class="error-banner">
      <div>{{ error }}</div>
      <AdminJsonViewer v-if="errorDetail" :value="errorDetail" class="mt-3" />
    </div>

    <Card v-if="resolvedSizing" class="mt-4">
      <template #title>Resolved Scenario</template>
      <template #content>
        <div class="resolved-grid">
          <div>
            <strong>Sizing:</strong>
            {{ resolvedSizing.title || resolvedSizing.id }}
          </div>
          <div>
            <strong>Scenario:</strong>
            {{ resolvedScenario?.title || resolvedScenario?.id }}
          </div>
          <div>
            <strong>Disposition:</strong>
            {{ resolvedSizing.disposition || "unknown" }}
          </div>
          <div><strong>Input type:</strong> {{ inputResolution }}</div>
          <div v-if="inputResolution === 'sizing'">
            <RouterLink :to="`/sizings/${resolvedSizing.id}/reports`">
              Open engineer sizing report
            </RouterLink>
          </div>
        </div>
        <p>Quote status refers to the linked commercial record. Calculations use the currently saved scenario revision, not a reconstructed quote-time or installed field design.</p>
        <ul v-if="selectedQuoteContext.length"><li v-for="quote in selectedQuoteContext" :key="quote.id"><RouterLink :to="`/quote/${quote.id}/quote`">{{ quote.status }} quote · version {{ quote.version_number }}</RouterLink></li></ul>
      </template>
    </Card>

    <Card v-if="comparisonReady" class="mt-4 comparison-card">
      <template #title>Engineer vs AI Equipment</template>
      <template #subtitle>
        Each AI column shows the first-ranked option for that priority. Match
        status compares ordered catalog selections and available assembly details, including quantities/stages. It does not measure design quality.
      </template>
      <template #content>
        <DataTable
          :value="equipmentComparisonRows"
          responsive-layout="scroll"
          class="comparison-table"
        >
          <Column field="equipment" header="Equipment" frozen />
          <Column header="Engineer sizing">
            <template #body="{ data }">
              <div class="comparison-value">
                <span>{{ data.engineer.label }}</span>
                <Tag value="Baseline" severity="info" />
              </div>
            </template>
          </Column>
          <Column
            v-for="lane in availableComparisonLanes"
            :key="lane"
            :header="laneLabel(lane)"
          >
            <template #body="{ data }">
              <div class="comparison-value">
                <span>{{ data.ai[lane]?.label || "Unavailable" }}</span>
                <Tag
                  :value="
                    selectionsMatch(data.engineer, data.ai[lane])
                       ? 'Same selection'
                      : 'Changed'
                  "
                  :severity="
                    selectionsMatch(data.engineer, data.ai[lane])
                      ? 'success'
                      : 'warning'
                  "
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <Card v-if="comparisonReady" class="mt-4 comparison-card">
      <template #title>Calculated Performance Comparison</template>
      <template #subtitle>
        The engineer baseline and every AI option are recalculated by the same
        sizing engine.
      </template>
      <template #content>
        <DataTable
          :value="metricComparisonRows"
          responsive-layout="scroll"
          class="comparison-table"
        >
          <Column field="metric" header="Metric" frozen />
          <Column field="unit" header="Unit" />
          <Column header="Engineer sizing">
            <template #body="{ data }">
              {{ formatMetric(data.engineer) }}
            </template>
          </Column>
          <Column
            v-for="lane in availableComparisonLanes"
            :key="lane"
            :header="laneLabel(lane)"
          >
            <template #body="{ data }">
              <div class="metric-value">
                <span>{{ formatMetric(data.ai[lane]) }}</span>
                <small>{{
                  formatDifference(data.ai[lane], data.engineer)
                }}</small>
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <Card v-if="result" class="mt-4">
      <template #title>Recommendation evidence</template>
      <template #subtitle>
        {{ availableComparisonLanes.length }} returned strategy options ·
        {{ result.blockingReasons.length }} blockers. Candidate-attempt totals are not included in the public response.
      </template>
      <template #content>
        <div class="summary-grid">
          <div>
            <strong>Baseline trace:</strong>
            {{ result.baselineTraceId || "none" }}
          </div>
          <div>
            <strong>Recommendation job:</strong> {{ comparisonJobId || 'Not supplied' }}
          </div>
        </div>

        <div v-if="result.rankingStatus === 'disabled'" class="warning-banner">
          {{ result.rankingMessage || "Grouped ranking options are disabled." }}
        </div>

        <Card v-if="result.recommendedOption" class="recommended-card">
          <template #title>Overall Recommended Option</template>
          <template #subtitle>
            {{ laneLabel(result.recommendedOption.lane) }} rank #{{
              result.recommendedOption.rank
            }}
            · Score {{ result.recommendedOption.score }} · Trace
            {{ result.recommendedOption.traceId }}
          </template>
          <template #content>
            <div class="chip-list">
              <span
                v-for="[name, score] in Object.entries(
                  result.recommendedOption.scoreBreakdown
                )"
                :key="name"
                class="chip"
              >
                {{ name }}: {{ score }}
              </span>
            </div>
          </template>
        </Card>

        <div v-if="showEmptyRankingState" class="warning-banner">
          The sizing resolved and candidate generation returned, but no grouped
          ranking options are available. Review blockers and rejected candidates
          in the raw JSON.
        </div>

        <Panel header="Alternative AI Options" class="mt-3">
          <AiDosOptionLanes :lane-entries="optionLaneEntries" />
        </Panel>

        <Panel
          v-if="result.blockingReasons.length"
          header="Blocking Reasons"
          class="mt-3"
        >
          <AdminJsonViewer :value="result.blockingReasons" />
        </Panel>

        <Panel
          v-if="result.candidateFallbacks?.length"
          header="Reused Baseline Components"
          class="mt-3"
        >
          <p class="muted-copy">
            AI DOS reused existing non-primary equipment where material search
            returned no replacement candidates. Displayed options are still
            calculation-verified.
          </p>
          <AdminJsonViewer :value="result.candidateFallbacks" />
        </Panel>
      </template>
    </Card>

    <Panel v-if="result" header="Raw Ranking JSON" class="mt-4">
      <div class="artifact-actions">
        <Button
          label="Copy JSON"
          class="p-button-text"
          @click="copyJson(result)"
        />
      </div>
      <AdminJsonViewer :value="result" />
    </Panel>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useToast } from "primevue/usetoast";
import MultiSelect from "primevue/multiselect";
import { fetchAIEquipmentSuggestions, type AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";
import AIDesignDiagnostics from "@/components/ai/AIDesignDiagnostics.vue";

import {
  generateCandidates,
  runSizingCalculationAction,
  runAgentCoreAdminOperation,
  fetchSizingReportContext,
  fetchSizingTrace,
  listCompletedSizings,
  lookupCompletedSizing,
  type CandidateGenerationResponse,
  type CompletedSizingLookupResponse,
  type RankedCandidateOption,
  type SizingTraceResponse
} from "@/libs/aiDos/adminApi";
import {
  AI_DOS_LANES,
  copyJsonToClipboard,
  errorMessage,
  laneLabel,
  type AiDosLane
} from "@/libs/aiDos/adminShared";
import {
  buildEquipmentComparisonRows,
  buildMetricComparisonRows,
  selectionsMatch
} from "@/libs/aiDos/adminSizingComparison";
import type { ScenarioContext } from "@/serverSizing/types";
import AiDosOptionLanes from "./AiDosOptionLanes.vue";
import AdminJsonViewer from "./AdminJsonViewer.vue";

const toast = useToast();
const sizingOrScenarioId = ref("");
const diagnosticOptions = ref<AIEquipmentSuggestionOption[]>([]);
const laneDiagnostics = ref<Record<string, unknown> | null>(null);
const selectedSizingId = ref<string | null>(null);
const minimumCandidates = ref(10);
type ReferenceCase = { id: string; title?: string; ai_recommendation?: unknown; sizing: { id: string; title?: string; disposition?: string }; quotes: Array<{ id: string; status: string; version_number: number }> };
const referenceCases = ref<ReferenceCase[]>([]);
const referenceCount = ref(0);
const referenceSearch = ref('');
const cohort = ref('won');
const cohorts = [{ label: 'Won quotes', value: 'won' }, { label: 'Any quoted scenario', value: 'quoted' }, { label: 'Accepted / closed sizings', value: 'completed' }, { label: 'All saved scenarios', value: 'all' }];
const comparisonProgress = ref('');
const comparisonJobId = ref('');
const reviewLane = ref<AiDosLane>('performance');
const verdict = ref('needs_review');
const verdicts = ['same', 'better', 'worse', 'mixed', 'not_comparable', 'needs_review'];
const curation = ref('unreviewed');
const curations = ['unreviewed', 'candidate', 'exclude'];
const reviewFlags = ref<string[]>([]);
const flagOptions = ['equipment_mismatch', 'stage_or_quantity', 'operating_point', 'hydraulics', 'electrical', 'gas_handling', 'materials_compatibility', 'missing_data', 'citation_quality', 'runtime_failure', 'good_alternative', 'human_baseline_issue'];
const reviewNotes = ref('');
const savingReview = ref(false);
const reviewMessage = ref('');
const reviews = ref<Record<string, unknown>[]>([]);
async function loadReviews() {
  if (!resolvedScenario.value?.id) return;
  try { reviews.value = (await runAgentCoreAdminOperation<{ reviews: Record<string, unknown>[] }>('list_sizing_reviews', { scenarioId: resolvedScenario.value.id })).reviews; }
  catch (e) { reviewMessage.value = `Review history unavailable: ${errorMessage(e)}`; }
}
async function saveAssessment() {
  savingReview.value = true;
  try {
    await runAgentCoreAdminOperation('save_sizing_review', { scenarioId: resolvedScenario.value?.id, jobId: comparisonJobId.value,
      baselineTraceId: result.value?.baselineTraceId, candidateTraceId: topOptions.value[reviewLane.value]?.traceId,
      sourceFingerprint: (scenarioContext.value?.source as { inputFingerprint?: string })?.inputFingerprint,
      lane: reviewLane.value, verdict: verdict.value, flags: reviewFlags.value, notes: reviewNotes.value, curation: curation.value });
    reviewMessage.value = 'Assessment saved. Training approval remains separate.';
    await loadReviews();
  } catch (e) { reviewMessage.value = errorMessage(e); }
  finally { savingReview.value = false; }
}
const isRunning = ref(false);
const isLoadingSizings = ref(false);
const error = ref<string | null>(null);
const errorDetail = ref<string | null>(null);
const sizingListError = ref<string | null>(null);
const result = ref<CandidateGenerationResponse | null>(null);
const scenarioContext = ref<ScenarioContext | null>(null);
const baselineCalculation = ref<SizingTraceResponse | null>(null);
const completedSizings = ref<CompletedSizingLookupResponse["sizings"]>([]);
const resolvedSizing = ref<
  CompletedSizingLookupResponse["sizings"][number] | null
>(null);
const inputResolution = ref<"sizing" | "scenario" | "unknown">("unknown");

const resolvedScenario = computed(
  () => resolvedSizing.value?.scenarios[0] || null
);
const selectedQuoteContext = computed(() => referenceCases.value.find(s => s.id === resolvedScenario.value?.id)?.quotes || []);
const optionLaneEntries = computed(
  () =>
    Object.entries(result.value?.options || {}) as Array<
      ["performance" | "runlife" | "cost", RankedCandidateOption[]]
    >
);
const topOptions = computed(
  () =>
    Object.fromEntries(
      AI_DOS_LANES.map(lane => [
        lane,
        result.value?.options?.[lane]?.[0]
      ]).filter(([, option]) => Boolean(option))
    ) as Partial<Record<AiDosLane, RankedCandidateOption>>
);
const availableComparisonLanes = computed(() =>
  AI_DOS_LANES.filter(lane => topOptions.value[lane])
);
const comparisonReady = computed(
  () =>
    Boolean(scenarioContext.value && baselineCalculation.value) &&
    availableComparisonLanes.value.length > 0
);
const equipmentComparisonRows = computed(() =>
  scenarioContext.value
    ? buildEquipmentComparisonRows(scenarioContext.value, topOptions.value)
    : []
);
const metricComparisonRows = computed(() =>
  buildMetricComparisonRows(
    baselineCalculation.value?.metrics || {},
    topOptions.value
  )
);
const completedSizingOptions = computed(() =>
  referenceCases.value
    .map(scenario => ({
      id: scenario.id,
      label: `${scenario.sizing.title || 'Untitled sizing'} · ${scenario.title || 'Untitled scenario'} · ${scenario.quotes.map(q => `${q.status} v${q.version_number}`).join(', ') || 'No linked quote'}${scenario.ai_recommendation ? ' · AI provenance' : ''}`
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
);
const showEmptyRankingState = computed(
  () =>
    Boolean(result.value) &&
    result.value?.rankingStatus !== "disabled" &&
    optionLaneEntries.value.every(([, options]) => options.length === 0) &&
    !result.value?.blockingReasons.length
);

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const UUID_EXACT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const normalizeSizingOrScenarioInput = (value: string) => {
  const trimmed = value.trim().replace(/^['"]|['"]$/g, "");
  const scenarioMatch = trimmed.match(/\/scenarios\/([^/?#]+)/i);
  if (scenarioMatch?.[1]) return scenarioMatch[1].replace(/\/+$/, "");
  const sizingMatch = trimmed.match(/\/sizings\/([^/?#]+)/i);
  if (sizingMatch?.[1]) return sizingMatch[1].replace(/\/+$/, "");
  const uuidMatches = [...trimmed.matchAll(UUID_PATTERN)].map(
    match => match[0]
  );
  return (uuidMatches.at(-1) || trimmed).replace(/\/+$/, "");
};

const loadCompletedSizingOptions = async () => {
  isLoadingSizings.value = true;
  sizingListError.value = null;
  try {
    const response = await runAgentCoreAdminOperation<{ cases: ReferenceCase[]; aggregate: { aggregate: { count: number } } }>('evaluation_cases', { cohort: cohort.value, search: referenceSearch.value, limit: 100 });
    referenceCases.value = response.cases;
    referenceCount.value = response.aggregate.aggregate.count;
  } catch (err) {
    sizingListError.value = errorMessage(err);
  } finally {
    isLoadingSizings.value = false;
  }
};

const selectCompletedSizing = () => {
  if (selectedSizingId.value) sizingOrScenarioId.value = selectedSizingId.value;
};

const runValidation = async () => {
  isRunning.value = true;
  error.value = null;
  errorDetail.value = null;
  result.value = null;
  comparisonJobId.value = '';
  diagnosticOptions.value = [];
  laneDiagnostics.value = null;
  reviews.value = [];
  reviewMessage.value = '';
  scenarioContext.value = null;
  baselineCalculation.value = null;
  resolvedSizing.value = null;
  inputResolution.value = "unknown";
  let usedScenarioFallback = false;
  try {
    const normalizedInput = normalizeSizingOrScenarioInput(
      sizingOrScenarioId.value
    );
    if (!normalizedInput) {
      throw new Error(
        "Enter a sizing ID, scenario ID, or sizing/scenario URL."
      );
    }
    if (!UUID_EXACT_PATTERN.test(normalizedInput)) {
      throw new Error(
        `"${normalizedInput}" is not a UUID. Paste a sizing or scenario UUID, or a /sizings/... URL containing one.`
      );
    }
    const picked = referenceCases.value.find(s => s.id === normalizedInput);
    const lookup = picked ? { sizings: [{ ...picked.sizing, scenarios: [{ id: picked.id, title: picked.title }] }] } : await lookupCompletedSizing(normalizedInput);
    const sizing = lookup.sizings[0];
    let scenarioId = sizing?.scenarios[0]?.id;
    if (sizing && !scenarioId) {
      throw new Error("Completed sizing has no active scenario to rank.");
    }
    let nextResolvedSizing: CompletedSizingLookupResponse["sizings"][number];
    let nextInputResolution: "sizing" | "scenario";
    if (sizing && scenarioId) {
      nextResolvedSizing = sizing;
      nextInputResolution = "sizing";
    } else {
      usedScenarioFallback = true;
      scenarioId = normalizedInput;
      nextResolvedSizing = {
        id: "direct-scenario-input",
        title: "Direct scenario input",
        disposition: null,
        pumping_type: "esp_on_shore",
        scenarios: [{ id: scenarioId }]
      };
      nextInputResolution = "scenario";
    }
    const fetchedContext = await fetchSizingReportContext(scenarioId);
    const baseline = await runSizingCalculationAction({ scenarioId, calculationMode: 'baseline_existing_equipment', persistTrace: true });
    const response = await fetchAIEquipmentSuggestions({ scenarioId }, progress => {
      comparisonProgress.value = progress.stage || 'Queued';
      if (progress.jobId) comparisonJobId.value = progress.jobId;
    });
    const options: CandidateGenerationResponse['options'] = { performance: [], runlife: [], cost: [] };
    laneDiagnostics.value = response.laneDiagnostics || null;
    diagnosticOptions.value = Object.values(response.options).filter((option): option is AIEquipmentSuggestionOption => Boolean(option));
    for (const lane of AI_DOS_LANES) {
      const option = response.options[lane];
      if (!option?.sizingDraft) continue;
      options[lane] = [{ id: option.traceId || lane, lane, rank: 1, score: option.metrics.score || 0,
        scoreBreakdown: option.scoreBreakdown || {}, scoreWeights: {}, confidence: 'low',
        pros: option.pros || [], cons: option.cons || [], warnings: option.warnings.map(code => ({ code })),
        hardFailures: option.incomplete ? [{ code: 'incomplete_design' }] : [], metricSummary: option.incomplete ? {} : option.verifiedMetrics || {},
        traceId: option.traceId, unavailableEnrichment: {}, candidatePackage: { ...option.sizingDraft, metadata: { designPlan: option.designPlan } } }];
    }
    const generatedResult: CandidateGenerationResponse = { scenarioId, options, baselineTraceId: baseline.traceId,
      displayableCandidates: [], rejectedCandidates: [], blockingReasons: (response.blockingReasons || []).map(reason => ({ ...reason })), catalogCounts: {} };
    result.value = generatedResult;
    scenarioContext.value = fetchedContext;
    if (!generatedResult.baselineTraceId) {
      throw new Error("AI sizing generation did not return a baseline trace.");
    }
    if (baseline.status === 'screened' || baseline.status === 'screen_rejected') throw new Error('Baseline must be a full calculation, not a screen.');
    baselineCalculation.value = { ...baseline, status: baseline.status };
    resolvedSizing.value = nextResolvedSizing;
    inputResolution.value = nextInputResolution;
    reviewLane.value = availableComparisonLanes.value[0] || 'performance';
    await loadReviews();
  } catch (err) {
    let message = errorMessage(err);
    const status =
      err && typeof err === "object" && "status" in err ? err.status : null;
    if (usedScenarioFallback && status === 404) {
      message +=
        " The ID did not match a completed ESP onshore sizing (dispositions accepted/closed)," +
        " so it was treated as a scenario ID, which also was not found.";
    }
    error.value = message;
    const payload =
      err && typeof err === "object" && "payload" in err ? err.payload : null;
    errorDetail.value = payload ? JSON.stringify(payload, null, 2) : null;
  } finally {
    isRunning.value = false;
  }
};

const copyJson = (value: unknown) => copyJsonToClipboard(toast, value);

const formatMetric = (value: number | null | undefined) =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(value)
    : "Unavailable";

const formatDifference = (
  candidate: number | null | undefined,
  baseline: number | null | undefined
) => {
  if (typeof candidate !== "number" || typeof baseline !== "number") return "";
  const difference = candidate - baseline;
  const sign = difference > 0 ? "+" : "";
  return `Δ ${sign}${formatMetric(difference)}`;
};

onMounted(loadCompletedSizingOptions);
</script>

<style scoped lang="scss">
.page-header {
  max-width: 58rem;
  margin-bottom: 1rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
  }
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.form-grid,
.resolved-grid,
.summary-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
}

.sizing-picker {
  align-items: end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(16rem, 1fr) auto;
  margin-bottom: 1rem;
}

.refresh-button {
  margin-bottom: 1.35rem;
}

.list-error {
  color: var(--red-600);
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 700;

  small {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
    font-weight: 400;
    line-height: 1.35;
  }
}

.error-banner {
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 0.5rem;
  color: var(--red-700);
  margin: 1rem 0;
  padding: 1rem;
}

.error-detail {
  background: color-mix(in srgb, var(--red-50), var(--surface-ground) 35%);
  border-radius: 0.35rem;
  color: inherit;
  margin: 0.75rem 0 0;
  max-height: 16rem;
  overflow: auto;
  padding: 0.75rem;
  white-space: pre-wrap;
}

.warning-banner {
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  border-radius: 0.5rem;
  color: var(--yellow-900);
  margin: 1rem 0;
  padding: 1rem;
}

.muted-copy {
  color: var(--text-color-secondary);
  margin: 0 0 0.75rem;
}

.recommended-card {
  margin: 1rem 0;
}

.chip-list {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.chip {
  border: 1px solid var(--surface-400);
  border-radius: 999px;
  color: var(--text-color);
  display: inline-flex;
  font-size: 0.78rem;
  line-height: 1.2;
  padding: 0.2rem 0.5rem;
}

.artifact-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.comparison-card :deep(.p-card-content) {
  padding-top: 0.5rem;
}

.comparison-table :deep(td) {
  min-width: 13rem;
  vertical-align: top;
}

.comparison-table :deep(td:first-child) {
  font-weight: 700;
  min-width: 10rem;
}

.comparison-value,
.metric-value {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  line-height: 1.35;
}

.metric-value small {
  color: var(--text-color-secondary);
}

.json-output {
  background: #111827;
  border: 1px solid #374151;
  border-radius: 0.5rem;
  color: #f9fafb;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.45;
  max-height: 28rem;
  overflow: auto;
  padding: 1rem;
  white-space: pre;
}

@media (max-width: 700px) {
  .sizing-picker {
    grid-template-columns: 1fr;
  }

  .refresh-button {
    justify-self: start;
    margin-bottom: 0;
  }
}
</style>
